<template>
  <div>
    <div
      v-if="contextMenu.show"
      ref="overlayRef"
      class="context-menu-overlay fixed inset-0"
      :class="`pointer-events-${overlayPointerEventsEnabled ? 'auto' : 'none'}`"
      @contextmenu.prevent.stop=""
    />
    <div
      ref="menuRoot"
      class="context-menu absolute z-10 appearance-none rounded-md shadow-md"
      :class="[{ active: contextMenu.show }, `size-${size}`]"
      :style="{ [openFromY]: `${position.y}px`, [openFromX]: `${position.x}px` }"
      data-testid="context-menu"
    >
      <div
        ref="innerContainer"
        class="options-container bg-white text-current cursor-pointer overflow-hidden"
        :style="{ width: `${MENU_WIDTH}rem` }"
      >
        <div class="flex items-start" :style="{ width: `${MENU_WIDTH * (submenus.length + 1)}rem` }">
          <menu class="main-menu" :style="{ width: `${MENU_WIDTH}rem` }">
            <li v-for="option in contextMenu.options" :key="option.id">
              <button
                class="option flex items-center"
                :class="{ 'internal-admin': option.internalAdminOnly }"
                type="button"
                :data-testid="`action-${option.id}`"
                @click="onClick(option as ContextAction)"
              >
                <div class="flex flex-1 flex-row justify-between items-center">
                  {{ option.label }}
                  <ExternalLinkIcon v-if="option.isExternal" class="h-3" stroke-width="3.5" />
                </div>
                <FontAwesomeIcon v-if="'subactions' in option && option.subactions" icon="chevron-right" />
              </button>
            </li>
          </menu>
          <menu
            v-for="(submenu, index) in submenus"
            :key="submenu.id"
            class="submenu"
            :style="{ width: `${MENU_WIDTH}rem`, left: `${(index + 1) * 100}%` }"
            :data-testid="submenu.id"
          >
            <button
              class="option flex gap-2 text-gray-600 text-sm items-center"
              :data-testid="`${submenu.id}-pop`"
              @click.prevent="popSubmenu"
            >
              <FontAwesomeIcon icon="chevron-left" />
              <div class="flex-1">{{ submenu.returnLabel }}</div>
            </button>
            <button
              v-for="option in submenu.options"
              :key="option.id"
              class="option flex"
              :class="{ 'internal-admin': option.internalAdminOnly }"
              @click.prevent="onClick(option)"
            >
              <div class="flex-1">{{ option.label }}</div>
              <FontAwesomeIcon v-if="'subactions' in option && option.subactions" icon="caret-right" />
            </button>
          </menu>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { DeepReadonly } from 'vue'
import { nextTick } from 'vue'
import { isChildOf } from '@/lib/utils'
import type { ContextAction, ContextMenu } from '@/composables/useContextMenu'
import { useContextMenuOverlay } from '@/composables/useContextMenuOverlay'
import ExternalLinkIcon from '~/components/icons/ExternalLinkIcon.svg'

const MENU_WIDTH = 16

const props = defineProps({
  contextMenu: {
    type: Object as PropType<DeepReadonly<ContextMenu>>,
    required: true,
  },
  size: {
    type: String as PropType<'xs' | 'sm' | 'md' | 'lg' | 'xl'>,
    default: 'md',
  },
})

const emit = defineEmits(['close'])

const menuRoot = ref<HTMLElement | undefined>()
const innerContainer = ref<HTMLElement | undefined>()
const position = ref<{ x: number; y: number }>({ x: 0, y: 0 })
const openFromX = ref<'left' | 'right'>('left')
const openFromY = ref<'top' | 'bottom'>('top')
const overlayRef = ref<HTMLElement | null>(null)

const { overlayPointerEventsEnabled } = useContextMenuOverlay({
  contextMenu: computed(() => props.contextMenu),
  onClose() {
    emit('close')
  },
  domElement: overlayRef,
})

onMounted(() => document.addEventListener('click', _removeActive))
onUnmounted(() => document.removeEventListener('click', _removeActive))

const submenus = ref<
  {
    id: string
    options: ContextAction[]
    returnLabel: string
  }[]
>([])

const pushSubmenu = async (option: ContextAction) => {
  if (!('subactions' in option)) return
  submenus.value = submenus.value.concat({
    id: `submenu-${option.id}`,
    returnLabel: option.subactions.returnLabel,
    options: option.subactions.actions,
  })

  await nextTick()
  const container = innerContainer.value
  const submenu = container?.querySelector('.submenu:last-child')
  if (!container || !submenu || !menuRoot.value) return
  const dimensions = submenu.getBoundingClientRect()
  container.scrollBy({ left: dimensions.width, behavior: 'smooth' })
  menuRoot.value.style.maxHeight = `${dimensions.height}px`
}

const popSubmenu = () => {
  const container = innerContainer.value
  const submenu = container?.querySelector('.submenu:nth-last-child(2), .main-menu:nth-last-child(2)')
  if (!container || !submenu || !menuRoot.value) return
  const dimensions = submenu.getBoundingClientRect()
  container.scrollBy({ left: -dimensions.width, behavior: 'smooth' })
  menuRoot.value.style.maxHeight = `${dimensions.height}px`

  setTimeout(() => {
    submenus.value = submenus.value.slice(0, submenus.value.length - 1)
  }, 400)
}

const select = (option: ContextAction) => {
  if (!('call' in option)) return
  option.call?.()
  close()
}

const close = () => {
  emit('close')
}

function _setPosition(dimensions: DOMRect): void {
  if (!props.contextMenu.origin) return

  let { x, y } = props.contextMenu.origin
  openFromX.value = 'left'
  openFromY.value = 'top'
  if (y + dimensions.height > window.innerHeight) {
    openFromY.value = 'bottom'
    y = window.innerHeight - y
  }
  if (x + dimensions.width > window.innerWidth) {
    openFromX.value = 'right'
    x = window.innerWidth - x
  }
  position.value = { x, y }
}

watch(
  [() => props.contextMenu.show, () => props.contextMenu.origin],
  ([show]) => {
    if (!menuRoot.value) return

    if (show) {
      const container = innerContainer.value
      const submenu = container?.querySelector('.main-menu')
      if (!container || !submenu) return
      const dimensions = submenu.getBoundingClientRect()
      _setPosition(dimensions)
      menuRoot.value.style.maxHeight = `${Math.min(dimensions.height, window.innerHeight)}px`
      menuRoot.value.style.maxWidth = `${MENU_WIDTH}rem`
    } else {
      setTimeout(() => (submenus.value = []), 400)
      menuRoot.value.style.maxHeight = `0px`
      menuRoot.value.style.maxWidth = `0px`
    }
  },
  { flush: 'post' }
)

const onClick = (option: ContextAction) => {
  if ('subactions' in option) {
    pushSubmenu(option)
  } else {
    select(option)
  }
}

const _removeActive = (e: MouseEvent) => {
  if (isChildOf(e.target as Node, menuRoot.value)) {
    return
  }
  if (props.contextMenu.show) {
    close()
  }
}
</script>

<style lang="stylus" scoped>
.context-menu
  max-height 0
  max-width 0
  transition max-height 0.3s ease-in-out, max-width 0.3s 0.15s ease-in-out
  overflow hidden
  pointer-events none

  menu
    @apply p-0 m-0

  button.option
    @apply bg-transparent text-copper-800 text-left px-4 py-2 w-full
    &:hover
      @apply bg-copper-100 text-copper-700
    &.internal-admin
      @apply text-gold-900 font-medium

  &::-webkit-scrollbar
    width 8px
    background rgba(0,0,0,0.12)
    border-radius 4px

  &::-webkit-scrollbar-thumb
    background rgba(0,0,0,0.12)
    border-radius 4px

  &.active
    pointer-events auto
    transition max-height 0.3s 0.15s ease-in-out, max-width 0.4s ease-in-out
</style>
